/**
 * Role / permission helpers.
 *
 * Real role list comes from `docs/specs/03-database-spec.md` `user_roles`
 * and `01-mvp-v1-planning-spec.md` §9. The role identifiers below are the
 * snake_case wire format the backend returns under `/api/me`.
 */

export type BuiltInRole = 'system_admin' | 'supplier_admin' | 'processor_admin' | 'platform_admin';

export type EfsRole = BuiltInRole | (string & {});
export type EfsPermission = string;

export interface CurrentUser {
  id: string;
  username: string;
  displayName?: string;
  email?: string;
  roles: EfsRole[];
  permissions?: EfsPermission[];
  facilityId?: string | null;
  companyId?: string | null;
  partyType?: 'supplier' | 'processor' | 'platform' | string | null;
  simulatedBy?: {
    id: string;
    email: string;
    displayName: string;
  } | null;
}

export function hasRole(user: CurrentUser | null | undefined, role: string): boolean {
  return !!user?.roles?.includes(role);
}

export function hasPermission(
  user: CurrentUser | null | undefined,
  permission: EfsPermission,
): boolean {
  if (!user) return false;
  const granted = user.permissions ?? [];
  return (
    user.roles.includes('system_admin') ||
    granted.includes('system.admin') ||
    granted.some((p) => p === permission || permissionImplies(p, permission))
  );
}

export function hasAnyPermission(
  user: CurrentUser | null | undefined,
  permissions: EfsPermission[],
): boolean {
  return permissions.some((permission) => hasPermission(user, permission));
}

export function hasAnyRole(
  user: CurrentUser | null | undefined,
  roles: readonly string[],
): boolean {
  if (!user) return false;
  if (hasPermission(user, 'system.admin')) return true;
  return roles.some(
    (r) =>
      user.roles.includes(r) ||
      (ROLE_PERMISSION_COVERAGE[r] ?? []).some((permission) => hasPermission(user, permission)),
  );
}

/** Throws when the user does not hold the role. Use as a guard in handlers. */
export function requireRole(user: CurrentUser | null | undefined, role: string): void {
  if (!hasRole(user, role)) {
    throw new Error(`PERMISSION_DENIED: requires role ${role}`);
  }
}

/**
 * Master approvals are a manager/admin privilege in the current demo backend.
 */
export function canApproveMasters(user: CurrentUser | null | undefined): boolean {
  return hasPermission(user, 'master.approve');
}

export function canActAsSupplier(user: CurrentUser | null | undefined): boolean {
  return (
    hasPermission(user, 'dispatch.request.act_as_supplier') &&
    (hasPermission(user, 'system.admin') || user?.partyType === 'platform')
  );
}

const ROLE_PERMISSION_COVERAGE: Record<string, string[]> = {
  supplier_admin: [
    'settings.manage',
    'dispatch.location_preset.read',
    'dispatch.location_preset.create',
    'dispatch.request.read',
    'dispatch.request.create',
    'dispatch.request.update_draft',
    'dispatch.request.submit',
    'dispatch.request.cancel',
    'report.manage',
  ],
  processor_admin: [
    'settings.manage',
    'master.manage',
    'dispatch.request.read',
    'dispatch.request.accept',
    'dispatch.request.reject_facility',
    'dispatch.request.fulfill',
    'receiving.manage',
    'processing.manage',
    'inventory.manage',
    'sales.manage',
    'audit.manage',
    'report.manage',
  ],
  platform_admin: [
    'settings.manage',
    'master.manage',
    'dispatch.manage',
    'receiving.manage',
    'processing.manage',
    'inventory.manage',
    'sales.manage',
    'pricing.manage',
    'settlement.manage',
    'audit.manage',
    'report.manage',
  ],
};

function permissionImplies(granted: string, needed: string): boolean {
  switch (granted) {
    case 'settings.manage':
      // role.read / role.manage are system_admin-only now — vendors manage
      // account permissions via checkboxes, never via role pages (mirrors the
      // backend narrowing; /api/roles 403s for vendor admins).
      return ['dashboard.read', 'user.manage'].includes(needed);
    case 'master.manage':
      return (
        needed.startsWith('master.') ||
        [
          'company.read',
          'company.manage',
          'supplier.read',
          'supplier.manage',
          'facility.read',
          'facility.create',
          'facility.update',
          'facility.delete',
        ].includes(needed)
      );
    case 'dispatch.manage':
      return needed.startsWith('dispatch.');
    case 'receiving.manage':
      return (
        needed.startsWith('receiving.') ||
        [
          'batch.read',
          'inbound.manage',
          'import.manage',
          'evidence.read',
          'evidence.manage',
        ].includes(needed)
      );
    case 'processing.manage':
      return (
        needed.startsWith('processing.') ||
        ['batch.read', 'sorting.manage', 'dismantling.manage', 'secondary_process.manage'].includes(
          needed,
        )
      );
    case 'inventory.manage':
      return ['batch.read', 'inventory.manage', 'mass_balance.read'].includes(needed);
    case 'sales.manage':
      // §10.2: the processor sales bundle never implies the PLATFORM authority to
      // decide buyer proposals — that stays an explicit grant. Mirrors backend
      // auth/model.rs and the report.manage narrowing.
      return (
        (needed.startsWith('sales.') && needed !== 'sales.buyer_proposal.decide') ||
        ['batch.read', 'shipment.manage'].includes(needed)
      );
    case 'pricing.manage':
      return (
        needed.startsWith('estimation.') ||
        needed.startsWith('valuation.') ||
        [
          'market_reference.input',
          'market_price.confirm',
          'pricing.manage',
          'quotation.getit.confirm',
          'freight.confirm',
          'profit_share.confirm',
        ].includes(needed)
      );
    case 'settlement.manage':
      return needed.startsWith('settlement.') || needed === 'monthly_close.approve';
    case 'audit.manage':
      return (
        needed.startsWith('approval.') ||
        needed.startsWith('audit.') ||
        needed.startsWith('correction.') ||
        [
          'dashboard.audit.read',
          'evidence.read',
          'evidence.manage',
          'exception.manage',
          'mass_balance.read',
          'mass_balance.manage',
          'audit_package.manage',
        ].includes(needed)
      );
    case 'report.manage':
      // External (price-free) report only; internal analysis must be granted explicitly.
      return needed.startsWith('report.external_monthly.');
    case 'evidence.manage':
      return needed === 'evidence.read';
    case 'file_library.manage':
      return ['file_library.read', 'file_library.upload', 'file_library.archive'].includes(needed);
    case 'mass_balance.manage':
      return needed === 'mass_balance.read';
    default:
      return false;
  }
}
